/**
 * Route manifest + RouterProvider for apps/admin.
 *
 * Per CLAUDE.md "Frontend (Sparta)" non-negotiable rule §2: every
 * route declares an access kind. The `ROUTE_MANIFEST` array below is
 * the single source of truth — `createBrowserRouter` consumes it,
 * and the route-manifest spec asserts every entry's `access` field.
 *
 * Public SPA routes are limited to the auth/bootstrap allow-list
 * (`PUBLIC_ALLOWLIST`). Backend API paths such as `/auth/csrf-token`
 * are handled by Vite proxy / Workbox deny-list coverage, not by this
 * route manifest.
 */

import * as React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';
import type { PermissionSlug } from '@artemis/contracts';
import { LoginPage } from './auth/LoginPage.js';
import { AuthenticatedRootPage } from './AuthenticatedRootPage.js';
import { NotFoundPage } from '../pages/NotFoundPage.js';
import { ErrorPage } from '../pages/ErrorPage.js';
import { PasswordResetRoute } from './auth/PasswordResetRoute.js';
import { AcceptInvitationRoute } from './auth/AcceptInvitationRoute.js';
import { AcceptPlatformInvitationRoute } from './auth/AcceptPlatformInvitationRoute.js';
import { VerifyEmailRoute } from './auth/VerifyEmailRoute.js';
import {
  UsersListPage,
  UserDetailPage,
  UsersWizardPage,
  PortalPage,
  InvitationsListPage,
} from '../modules/identity/index.js';
import {
  AdminOverviewPage,
  CompaniesListPage,
  CompanyCreateWizardPage,
  CompanyDetailPage,
  StaffListPage,
  StaffAddPage,
  StaffDetailPage,
  ImportsQueuePage,
  ImportDetailPage,
  AdminComingSoonPanel,
} from '../modules/platform/index.js';
import {
  PropertiesPage,
  PropertyCreateWizardPage,
  PropertyDetailPage,
  PropertyImportPage,
  UnitDetailPage,
} from '../modules/property/index.js';
import {
  ReservationsPage,
  ReservationDetailPage,
  CreateReservationPage,
  RecordChannelReservationPage,
} from '../modules/booking/index.js';
import { AdminShell } from '../widgets/admin-shell/index.js';
import { DashboardHome } from '../widgets/dashboard/index.js';

export type RouteAccess =
  | { kind: 'public' }
  | { kind: 'authenticated' }
  | {
      kind: 'organization-permission';
      permissions: readonly PermissionSlug[];
    }
  | { kind: 'group-access' };

export interface RouteManifestEntry {
  path: string;
  access: RouteAccess;
  element: React.ReactElement;
  errorElement?: React.ReactElement;
  /**
   * Optional nested routes rendered inside the parent's element via
   * React Router's `<Outlet />`. Children inherit the parent's
   * access kind. The route-manifest spec validates access kinds at
   * the top level only; children that need a *stricter* access
   * (e.g. `organization-permission`) declare it explicitly.
   */
  children?: readonly RouteManifestEntry[];
}

export const PUBLIC_ALLOWLIST: readonly string[] = [
  '/login',
  '/password-reset',
  '/accept-invitation',
  '/accept-platform-invitation',
  '/verify-email',
  '/callback',
  '/error',
];

export function isAllowedPublicRoute(path: string): boolean {
  if (path === '*') return true;
  return PUBLIC_ALLOWLIST.some(
    (allowed) => path === allowed || path.startsWith(`${allowed}/`),
  );
}

export const ROUTE_MANIFEST: readonly RouteManifestEntry[] = [
  {
    path: '/login',
    access: { kind: 'public' },
    element: <LoginPage />,
  },
  {
    // Single FE path serves both the email-request form and the
    // token-bearing consume form — the email link template is fixed
    // at `${FE_URL}/password-reset?token=…`. PasswordResetRoute reads
    // the query param and dispatches to the appropriate page.
    path: '/password-reset',
    access: { kind: 'public' },
    element: <PasswordResetRoute />,
  },
  {
    // Token-bearing invitation consume — email link is fixed at
    // `${FE_URL}/accept-invitation?token=…`. AcceptInvitationRoute
    // reads the query param and renders AcceptInvitationConsumePage.
    path: '/accept-invitation',
    access: { kind: 'public' },
    element: <AcceptInvitationRoute />,
  },
  {
    // SCRUM-131: PUBLIC platform-staff invitation consume — a SEPARATE
    // flow from `/accept-invitation` above. The BE smtp renderer points
    // platform invitees at `${FE_URL}/accept-platform-invitation?token=…`.
    // It hits `/auth/platform-invitations/{preview,accept}` and ends at
    // `/login` (no session minted). Both flows coexist.
    path: '/accept-platform-invitation',
    access: { kind: 'public' },
    element: <AcceptPlatformInvitationRoute />,
  },
  {
    // Token-bearing email-verification consume — email link is fixed
    // at `${FE_URL}/verify-email?token=…`. VerifyEmailRoute reads the
    // query param and auto-submits via VerifyEmailConsumePage. The
    // legacy BE GET handler remains for in-flight emails sent before
    // the FE migration finished.
    path: '/verify-email',
    access: { kind: 'public' },
    element: <VerifyEmailRoute />,
  },
  {
    // SCRUM-113: post-auth portal chooser. PortalPage internally
    // routes single-portal users to their home and unauthenticated
    // users to /login per auth-admin-20260522/AUTH_FLOW.md §3, so the
    // route's `access` is `authenticated` (the page handles the
    // unauthenticated branch with a navigation effect; we still
    // declare the access kind so the route-manifest spec stays
    // honest about who this surface targets).
    path: '/portal',
    access: { kind: 'authenticated' },
    element: <PortalPage />,
  },
  {
    path: '/',
    access: { kind: 'authenticated' },
    element: <AuthenticatedRootPage />,
    // SCRUM-297 / MENU-1: AuthenticatedRootPage renders `<PmsShellV4 />`,
    // the single PMS shell, which surfaces the active child route inside
    // its scrolling `<main>` via `<Outlet/>`. The `path: ''` index child
    // below renders the dashboard home content (GreetingHero + WidgetGrid)
    // at `/`. (Previously the now-retired `DashboardPage` owned the Outlet
    // and inlined its own home content — SCRUM-25/58.)
    children: [
      // Index child: the dashboard home content, rendered inside
      // PmsShellV4's `<main>` at `/`. Authenticated (same kind as the
      // parent); the route-manifest spec asserts this `path: ''` entry.
      {
        path: '',
        access: { kind: 'authenticated' },
        element: <DashboardHome />,
      },
      // SCRUM-108 Phase 1: ratchet the Users subtree from the PR #134
      // `authenticated` placeholder to the typed permission slugs that
      // CLAUDE.md non-negotiable §2 requires. Route-level enforcement is
      // declarative today (permission-gate-guardian validates the spec);
      // page-level enforcement on the list endpoint surfaces 403 via the
      // `forbidden` branch of `useUsersList`.
      {
        path: 'users',
        access: {
          kind: 'organization-permission',
          permissions: ['identity.users.read'],
        },
        element: <UsersListPage />,
      },
      {
        // Pending-invitations roster. Lives under `/users` because
        // conceptually invitations are pre-membership records — they
        // graduate into the Users list on acceptance. `read` slug
        // gates the page; per-row Resend/Revoke buttons are
        // additionally gated by their own slugs inside the page.
        path: 'users/invitations',
        access: {
          kind: 'organization-permission',
          permissions: ['identity.invitations.read'],
        },
        element: <InvitationsListPage />,
      },
      {
        path: 'users/invite',
        access: {
          kind: 'organization-permission',
          // `identity.invitations.create` is the slug the backend
          // `InvitationsController.create` enforces (SCRUM-25). The legacy
          // `identity.users.invite` slug from migration `0005_authorization_expand`
          // pre-dates SCRUM-25, is never checked at any controller, and exists
          // only as a fixture-shaped catalogue entry. The FE must gate on
          // whatever the BE actually enforces — see the
          // `RequirePermission slug="identity.invitations.create"` wraps in
          // `UsersListPage` (invite button) and `UsersWizardPage` (whole form).
          permissions: ['identity.invitations.create'],
        },
        element: <UsersWizardPage />,
      },
      {
        path: 'users/:userId',
        access: {
          kind: 'organization-permission',
          permissions: ['identity.users.read'],
        },
        element: <UserDetailPage />,
      },
      // SCRUM-167 / SCRUM-168 (P0): Properties module FE shell. Four
      // placeholder routes scaffold the slice's surface area behind the
      // typed permission gates so the LeftMenu Properties slot resolves
      // in-portal instead of dead-ending on the 404 catch-all (per
      // SCRUM-114 retro). Per-surface tickets replace each
      // `NotConnectedYet` body with the real implementation — no rework
      // on the route manifest, the LeftMenu active state, or the
      // permission gate. The full phasing is:
      //
      //   SCRUM-169 P1.1 List page
      //   SCRUM-170 P1.2 Detail chrome + Overview + Status modal
      //   SCRUM-171 P1.3 Detail Pricing tab
      //   SCRUM-172 P1.4 Detail Owner tab
      //   SCRUM-173 P1.5 Detail Lifecycle/Archive tab
      //   SCRUM-174 P2.1 Spaces + Units + SIM (shells)
      //   SCRUM-175 P2.2 Channels (shell)
      //   SCRUM-176 P2.3 Content tab + AI generate stub
      //   SCRUM-177 P2.4 Amenities + Compliance + History (shells)
      //   SCRUM-178 P3.1 Create wizard chrome + wired steps
      //   SCRUM-179 P3.2 Create wizard shell steps (Spaces, Units)
      //   SCRUM-180 P4   Unit detail page (shell)
      //   SCRUM-181 P5   Bulk import UI shell
      //   SCRUM-182 P6   Mobile reflow + visual baselines + a11y sweep
      {
        path: 'properties',
        access: {
          kind: 'organization-permission',
          permissions: ['property.read'],
        },
        element: <PropertiesPage />,
      },
      {
        path: 'properties/new',
        access: {
          kind: 'organization-permission',
          permissions: ['property.write'],
        },
        element: <PropertyCreateWizardPage />,
      },
      {
        // SCRUM-181 (P5) bulk import — shell. The route lives under
        // /properties/import so the SCRUM-169 PropertiesPage's
        // "↑ Import CSV" button has a destination instead of an
        // alert stub. Gated by property.write (uploading a CSV
        // creates properties).
        path: 'properties/import',
        access: {
          kind: 'organization-permission',
          permissions: ['property.write'],
        },
        element: <PropertyImportPage />,
      },
      {
        path: 'properties/:id',
        access: {
          kind: 'organization-permission',
          permissions: ['property.read'],
        },
        element: <PropertyDetailPage />,
      },
      {
        path: 'properties/:id/units/:unitId',
        access: {
          kind: 'organization-permission',
          permissions: ['property.read'],
        },
        element: <UnitDetailPage />,
      },
      // SCRUM-333 (SCRUM-332 epic, P0): Booking / Reservations module FE
      // shell. Three honest-shell routes scaffold the slice's surface area
      // behind the typed `booking.reservations.*` gates so the surface
      // resolves in-portal instead of dead-ending on the 404 catch-all.
      // Per-surface tickets replace each `NotConnectedYet` body with the
      // real implementation — no rework on the route manifest, the
      // LeftMenu active state, or the permission gate:
      //
      //   SCRUM-334 reservations grid   (ReservationsPage body)
      //   SCRUM-335 reservation detail  (ReservationDetailPage body)
      //   SCRUM-337 create reservation  (CreateReservationPage body)
      //
      // `reservations/new` and `reservations/record-channel` are declared
      // BEFORE `reservations/:id` so the static segments match ahead of the
      // dynamic `:id` param.
      //
      // NAV NOTE: the `reservations` nav id is now LIVE — `nav-model.ts` lists
      // it in `NAV_V4_ROUTABLE` (enabled + navigable in the desktop rail and the
      // mobile drawer) and `pathnameToNavV4Active` maps `/reservations*` to the
      // `reservations` id (active highlight). `/reservations*` is both
      // URL-reachable (behind its permission) and menu-navigable.
      {
        path: 'reservations',
        access: {
          kind: 'organization-permission',
          permissions: ['booking.reservations.read'],
        },
        element: <ReservationsPage />,
      },
      {
        path: 'reservations/new',
        access: {
          kind: 'organization-permission',
          permissions: ['booking.reservations.write'],
        },
        element: <CreateReservationPage />,
      },
      {
        // SCRUM-350: manual channel-ingest recording surface — records a
        // LIVE non-integrated channel booking via the shipped ingest path.
        // Gated on the FULL set of three slugs it exercises (ingest submit +
        // the reservation-type/list reads + the property picker) so an
        // ingest-only custom role never lands on a 403-riddled page; see the
        // plan's "Access model" decision. Declared BEFORE `reservations/:id`
        // so the static segment wins over the dynamic `:id` param.
        path: 'reservations/record-channel',
        access: {
          kind: 'organization-permission',
          permissions: [
            'booking.reservations.ingest',
            'booking.reservations.read',
            'property.read',
          ],
        },
        element: <RecordChannelReservationPage />,
      },
      {
        path: 'reservations/:id',
        access: {
          kind: 'organization-permission',
          permissions: ['booking.reservations.read'],
        },
        element: <ReservationDetailPage />,
      },
      // SCRUM-302 / MENU-6: the Agency nav leaf routes to an honest placeholder.
      // There is no Agency/AI FE surface or agent backend yet (see the MENU-5
      // BE-need), and `ai.*` carries no authz slug, so the route is
      // `authenticated` until one lands — it graduates to a `modules/ai/` slice
      // when the real surface ships. The sidebar Agency leaf ALSO expands its
      // agent quick-list (SCRUM-301); this wires the "routes" half of that leaf.
      { path: 'agency', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Agency" /> },
      // SCRUM-114: PMS dashboard account-menu destinations. Placeholder
      // surfaces rendered in the dashboard layout so the shared account menu's
      // links resolve in-portal instead of dead-ending on the 404 catch-all.
      { path: 'account', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Account settings" /> },
      { path: 'notifications', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Notifications" /> },
      { path: 'help', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Help & support" /> },
    ],
  },
  {
    // SCRUM-116: Admin portal foundation. `<AdminShell />` owns the
    // entitlement gate (redirects users lacking the Admin portal to
    // `/`, the real PMS root — SCRUM-15 reviewer fix 2026-05-27, was
    // round-1 `/portal` which had no router entry) + the TopPill +
    // LeftRail + content `<Outlet />`. All nested routes inherit the
    // `authenticated` access kind at the route-manifest level; the
    // shell enforces the additional `me.portals.includes('Admin')`
    // check inside the render branch (build-mode-gated for the `null`
    // legacy fallback — see `shared/auth/admin-portal-entitlement.ts`).
    // Placeholder pages render `AdminComingSoonPanel` until the
    // per-surface tickets (SCRUM-26 Overview, SCRUM-119+ Companies,
    // SCRUM-123/124 Staff, SCRUM-125/126 Imports) land.
    path: '/admin',
    access: { kind: 'authenticated' },
    element: <AdminShell />,
    children: [
      { path: '', access: { kind: 'authenticated' }, element: <AdminOverviewPage /> },
      { path: 'companies', access: { kind: 'authenticated' }, element: <CompaniesListPage /> },
      { path: 'companies/new', access: { kind: 'authenticated' }, element: <CompanyCreateWizardPage /> },
      { path: 'companies/:id', access: { kind: 'authenticated' }, element: <CompanyDetailPage /> },
      { path: 'staff', access: { kind: 'authenticated' }, element: <StaffListPage /> },
      { path: 'staff/new', access: { kind: 'authenticated' }, element: <StaffAddPage /> },
      { path: 'staff/:id', access: { kind: 'authenticated' }, element: <StaffDetailPage /> },
      { path: 'imports', access: { kind: 'authenticated' }, element: <ImportsQueuePage /> },
      { path: 'imports/:id', access: { kind: 'authenticated' }, element: <ImportDetailPage /> },
      // SCRUM-114: account-menu destinations. Placeholder surfaces (no real
      // page yet) so the menu's Account settings / Notifications / Help links
      // resolve instead of dead-ending on the 404 catch-all.
      { path: 'account', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Account settings" /> },
      { path: 'notifications', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Notifications" /> },
      { path: 'help', access: { kind: 'authenticated' }, element: <AdminComingSoonPanel surface="Help & support" /> },
    ],
  },
  {
    path: '/error',
    access: { kind: 'public' },
    element: <ErrorPage />,
  },
  {
    path: '*',
    access: { kind: 'public' },
    element: <NotFoundPage />,
  },
];

function manifestEntryToRouteObject(entry: RouteManifestEntry): RouteObject {
  const route: RouteObject = {
    path: entry.path,
    element: entry.element,
    errorElement: entry.errorElement ?? <ErrorPage />,
  };
  if (entry.children !== undefined && entry.children.length > 0) {
    route.children = entry.children.map(manifestEntryToRouteObject);
  }
  return route;
}

export function buildRouter(): ReturnType<typeof createBrowserRouter> {
  const routes: RouteObject[] = ROUTE_MANIFEST.map(manifestEntryToRouteObject);
  return createBrowserRouter(routes);
}

export function AppRouter(): React.ReactElement {
  const router = React.useMemo(() => buildRouter(), []);
  return <RouterProvider router={router} />;
}
